import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useIntegrations } from "./useIntegrations";

import { ROUTES } from "@/constants/routes";
import { decodeSlug } from "@/features/integrations/utils/slug";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";

/**
 * Breadcrumbs hook for an integration module's client-level detail page.
 *
 * Builds: Integrations > <Integration display name> > <Module display name>
 *
 * @param moduleName Module name used for the i18n key `integrations.modules.names.<moduleName>`
 *   (e.g. "organic", "job-sync").
 */
export function useIntegrationModuleClientDetailsBreadcrumbs(
  moduleName: string,
) {
  const { t, i18n } = useTranslation();
  const { clientId, integrationName } = useParams<{
    clientId?: string;
    integrationName?: string;
  }>();
  const { tenantId } = useAuth();
  const integrationsQuery = useIntegrations({ tenantId, clientId });

  const decodedIntegrationName = integrationName
    ? decodeSlug(integrationName)
    : null;

  const integration =
    integrationsQuery.data?.find(
      (i) =>
        i.name.toLowerCase() === decodedIntegrationName?.toLowerCase(),
    ) ?? null;

  const integrationDisplayName =
    integration?.displayName && i18n.exists(integration.displayName)
      ? t(integration.displayName)
      : integration?.displayName;

  const moduleDisplayName = t(`integrations.modules.names.${moduleName}`);

  const breadcrumbs = useBreadcrumbs({
    items: clientId
      ? [
        {
          label: t("common.integrations"),
          href: `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}`,
        },
        {
          label: integrationDisplayName ?? t("common.integration"),
          href:
            clientId && integrationName
              ? `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${integrationName}`
              : undefined,
        },
        {
          label: moduleDisplayName,
        },
      ]
      : [
        { label: t("common.integrations"), href: `/${ROUTES.integrations}` },
        {
          label: integrationDisplayName ?? t("common.integration"),
          href: integrationName
            ? `/${ROUTES.integrations}/${integrationName}`
            : undefined,
        },
        {
          label: moduleDisplayName,
        },
      ],
  });

  return { breadcrumbs, integration };
}
